import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceProductListItem } from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type ProductSortField = 'code' | 'name' | 'lineOfBusiness' | 'effectiveFromUtc';

@Component({
  standalone: true,
  selector: 'ain-insurance-products-shell',
  imports: [AinDatePipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('insurance.products.title')" [description]="t('insurance.products.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.products.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>{{ t('insurance.filters.title') }}</summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.products.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.products.states.empty')" [message]="t('insurance.products.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('code')">{{ t('insurance.products.fields.code') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('name')">{{ t('insurance.products.fields.name') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('lineOfBusiness')">{{ t('insurance.products.fields.lineOfBusiness') }}</button></th>
                  <th>{{ t('insurance.products.fields.active') }}</th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('effectiveFromUtc')">{{ t('insurance.products.fields.effectiveFrom') }}</button></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.code }}</button></td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.lineOfBusiness }}</td>
                  <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.effectiveFromUtc | ainDate }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel{display:grid;gap:10px}
    .filters{border:1px solid #e5e7eb;border-radius:10px;padding:8px 10px;background:#f9fafb}
    .filter-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:8px}
    .filter-grid label{display:grid;gap:6px}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    .error{color:#b91c1c}
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceProductsShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly rows = signal<InsuranceProductListItem[]>([]);
  readonly selectedRows = signal<InsuranceProductListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<ProductSortField>('code');
  readonly sortOrder = signal<SortOrder>(1);

  liveStatusKey = liveStatusKey;

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listProducts().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceProductListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.rows()
      : this.rows().filter(row =>
        row.code.toLowerCase().includes(value)
        || row.name.toLowerCase().includes(value)
        || row.lineOfBusiness.toLowerCase().includes(value));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceProductListItem[] | InsuranceProductListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/products', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/products/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-products.csv',
      ['Code', 'Name', 'LineOfBusiness', 'IsActive', 'EffectiveFromUtc'],
      rows.map(item => [
        item.code,
        item.name,
        item.lineOfBusiness,
        item.isActive ? 'Yes' : 'No',
        item.effectiveFromUtc
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: ProductSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
